import React from 'react';
import { DeltaProps, DeltaSentiment } from '../../../types/DesignSystem/dataviz';
import { withClassName } from '../cn';

/**
 * Delta (§ 311) — a signed change, with its meaning attached.
 *
 * **The whole component is one decision: up is not always good.** "Conversions
 * +6.2%" and "median build time +0.6s" are the same arithmetic and opposite news.
 * Hardcoding green-for-up — which is what almost every dashboard does — makes the
 * second one render as an improvement, permanently and silently. So `sentiment` is
 * a prop, `up-is-bad` is a first-class value, and `neutral` exists for changes that
 * genuinely have no valence.
 *
 * **Zero is not a small rise.** `0` drops the arrow entirely and takes the neutral
 * tone, because a chip reading "↑ 0.0%" is a lie about direction.
 */

/* One decision, never stacked — the `fieldBorder` arrangement. The keys are
   sentiment × direction, so the mapping is exhaustive and readable as a table. */
const TONE: Record<DeltaSentiment, { up: string; down: string }> = {
  'up-is-good': { up: 'success', down: 'danger' },
  'up-is-bad': { up: 'danger', down: 'success' },
  neutral: { up: 'neutral', down: 'neutral' },
};

/* Tailwind reads source as text, so every class is a literal (see `dataviz.ts`). */
const PILL: Record<string, string> = {
  success: 'tw-bg-tone-success-tint tw-text-tone-success-ink',
  danger: 'tw-bg-tone-danger-tint tw-text-tone-danger-ink',
  neutral: 'tw-bg-tone-neutral-tint tw-text-tone-neutral-ink',
};

const QUIET: Record<string, string> = {
  success: 'tw-text-tone-success-ink',
  danger: 'tw-text-tone-danger-ink',
  neutral: 'tw-text-ink-secondary',
};

/**
 * The arrow, drawn rather than typed.
 *
 * `▲`/`↑` render in whatever the font has, which is a different shape and weight in
 * every fallback — and `Kbd` already learned that lesson printing "Meta". An inline
 * path on the icon family's 16 grid at the compact-mark stroke (§ 232's 0.094 band)
 * matches the rest of the system's glyphs.
 */
const Arrow = ({ up, testId }: { up: boolean; testId?: string }) => (
  <svg
    width={12}
    height={12}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    data-testid={testId}
    className={`tw-shrink-0 ${up ? '' : 'tw-rotate-180'}`}
  >
    <path
      d="M8 13V3M8 3L4 7M8 3L12 7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** `+6.2%` / `-0.8pt` / `+41 SKUs`. The sign is always explicit — "6.2%" is not a change. */
export const formatDelta = (
  value: number,
  format: 'percent' | 'point' | 'absolute',
  precision: number,
  unit: string,
): string => {
  if (!Number.isFinite(value)) return '—';
  const magnitude = Math.abs(value).toFixed(precision);
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  const suffix = format === 'percent' ? '%' : format === 'point' ? 'pt' : unit;
  return `${sign}${magnitude}${suffix}`;
};

const Delta = React.forwardRef<HTMLSpanElement, DeltaProps>(function Delta({
  value,
  sentiment = 'up-is-good',
  format = 'percent',
  unit = '',
  precision,
  quiet = false,
  label,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  const safe = Number.isFinite(value) ? value : 0;
  const decimals = precision ?? (format === 'absolute' ? 0 : 1);
  const flat = safe === 0;
  const up = safe > 0;

  const tone = flat ? 'neutral' : TONE[sentiment][up ? 'up' : 'down'];
  const text = formatDelta(safe, format, decimals, unit);

  /*
   * The announced string spells out the direction in words. A screen reader reading
   * "+6.2%" says "plus six point two percent", which is fine — but the ARROW carries
   * the direction visually and is aria-hidden, and for `up-is-bad` the COLOUR carries
   * the verdict and is not announced at all. Saying "down" and naming the measure is
   * what makes the two channels agree.
   */
  const spoken = flat
    ? `${label ? `${label}: ` : ''}no change`
    : `${label ? `${label}: ` : ''}${up ? 'up' : 'down'} ${text.replace('−', '').replace('+', '')}`;

  return (
    <span
      {...rest}
      ref={ref}
      data-testid={dataTestId}
      className={withClassName(
        'tw-inline-flex tw-items-center tw-gap-0.5 tw-text-caption-metadata tw-font-medium '
        + 'tw-tabular-nums tw-whitespace-nowrap tw-align-middle '
        + (quiet ? QUIET[tone] : `${PILL[tone]} tw-px-1.5 tw-py-0.5 tw-rounded-ctl `),
        className,
      )}
    >
      {/* The visible text is hidden from the a11y tree and replaced by `spoken`, so
          the two never disagree — the sr-only string IS the announcement. */}
      <span aria-hidden="true" className="tw-inline-flex tw-items-center tw-gap-0.5">
        {!flat && <Arrow up={up} testId={dataTestId ? `${dataTestId}-arrow` : undefined} />}
        <span data-testid={dataTestId ? `${dataTestId}-value` : undefined}>{text}</span>
      </span>
      <span className="tw-sr-only">{spoken}</span>
    </span>
  );
});

export default Delta;
